import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { AppIcon } from '@/components/AppIcon';
import { MARK_CODE, MARK_TEXT, WORKER_TYPE_TEXT, type Mark } from '@/hooks/hr';
import {
  MARKS,
  leaveTypesFor,
  lockReason,
  noWork,
  type LeaveType,
  type RegisterRow,
  type RowValues,
} from './attendanceRegister';

const KEY_MARK: Record<string, Mark> = {
  p: 'PRESENT',
  a: 'ABSENT',
  h: 'HALF_DAY',
  l: 'LEAVE',
  o: 'WEEKLY_OFF',
};

/**
 * The daily register: one row per person, marked from the keyboard.
 *
 * With a row focused, P A H L O set the mark and move to the next row (L
 * instead jumps to the leave type), Delete clears it, the arrow keys, Home and
 * End move, and Esc leaves a field or the row. Only one row is in the tab
 * order at a time, so Tab moves through that row's fields rather than walking
 * a hundred rows of buttons.
 */
export function AttendanceRegisterGrid({
  rows,
  valuesOf,
  isDirty,
  onChange,
  leaveTypes,
  canEdit,
}: {
  rows: RegisterRow[];
  valuesOf: (row: RegisterRow) => RowValues;
  isDirty: (row: RegisterRow) => boolean;
  onChange: (row: RegisterRow, patch: Partial<RowValues>) => void;
  leaveTypes: LeaveType[];
  canEdit: boolean;
}) {
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [active, setActive] = useState(0);
  const pendingLeaveFocus = useRef<string | null>(null);

  // After L is pressed the leave select appears on the next render; focus it then.
  useEffect(() => {
    const id = pendingLeaveFocus.current;
    if (!id) return;
    pendingLeaveFocus.current = null;
    bodyRef.current?.querySelector<HTMLSelectElement>(`select[data-leave-for="${id}"]`)?.focus();
  });

  const activeIndex = Math.min(active, Math.max(0, rows.length - 1));

  function focusRow(index: number) {
    if (rows.length === 0) return;
    const i = Math.min(rows.length - 1, Math.max(0, index));
    setActive(i);
    bodyRef.current?.querySelectorAll<HTMLTableRowElement>('tr[data-row]')[i]?.focus();
  }

  function setMark(row: RegisterRow, mark: Mark | null) {
    if (lockReason(row, canEdit)) return;
    const patch: Partial<RowValues> = { mark };
    if (mark === 'LEAVE') {
      const types = leaveTypesFor(row, leaveTypes);
      const current = valuesOf(row).leaveTypeId;
      patch.leaveTypeId = current ?? (types.length === 1 ? types[0]!.id : null);
      pendingLeaveFocus.current = row.employeeId;
    }
    onChange(row, patch);
  }

  function onRowKey(e: ReactKeyboardEvent<HTMLTableRowElement>, index: number, row: RegisterRow) {
    const target = e.target as HTMLElement;
    const typing = target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA';
    if (e.key === 'Escape') {
      e.preventDefault();
      if (target !== e.currentTarget) e.currentTarget.focus();
      else e.currentTarget.blur();
      return;
    }
    if (typing) {
      // Enter in a field goes back to the row, ready for the next key.
      if (e.key === 'Enter' && target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        e.currentTarget.focus();
      }
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    switch (e.key) {
      case 'ArrowDown':
        focusRow(index + 1);
        break;
      case 'ArrowUp':
        focusRow(index - 1);
        break;
      case 'Home':
        focusRow(0);
        break;
      case 'End':
        focusRow(rows.length - 1);
        break;
      case 'PageDown':
        focusRow(index + 10);
        break;
      case 'PageUp':
        focusRow(index - 10);
        break;
      case 'Delete':
      case 'Backspace':
        setMark(row, null);
        break;
      default: {
        const mark = KEY_MARK[e.key.toLowerCase()];
        if (!mark) return;
        setMark(row, mark);
        // Marking moves on to the next person, so a register is P, P, A, P…
        // Leave stays put: its type comes next.
        if (mark !== 'LEAVE' && !lockReason(row, canEdit) && index < rows.length - 1) focusRow(index + 1);
      }
    }
    e.preventDefault();
  }

  return (
    <div className="grid__wrap">
      <table className="table grid register" role="grid" aria-label="Daily register">
        <thead>
          <tr>
            <th className="register__flagcol">
              <span className="sr-only">Saved or locked</span>
            </th>
            <th style={{ width: 100 }}>Code</th>
            <th style={{ width: 220 }}>Name</th>
            <th style={{ width: 130 }}>Designation</th>
            <th style={{ width: 110 }}>Pay type</th>
            <th style={{ width: 230 }}>Mark</th>
            <th style={{ width: 170 }}>Leave</th>
            <th style={{ width: 90 }}>Overtime (hours)</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {rows.map((row, index) => {
            const v = valuesOf(row);
            const dirty = isDirty(row);
            const locked = lockReason(row, canEdit);
            const suggested = v.mark === null && !row.saved && !dirty ? row.suggestedMark : null;
            const types = leaveTypesFor(row, leaveTypes);
            const selected = index === activeIndex;
            return (
              <tr
                key={row.employeeId}
                data-row
                tabIndex={selected ? 0 : -1}
                aria-selected={selected}
                className={`grid__row${selected ? ' grid__row--selected' : ''}${locked ? ' register__row--locked' : ''}${dirty ? ' register__row--dirty' : ''}`}
                title={locked ?? undefined}
                onFocus={() => setActive(index)}
                onKeyDown={(e) => onRowKey(e, index, row)}
              >
                <td className="register__flagcol">
                  {row.lockedBy ? (
                    <span className="register__flag" title={`Pay run ${row.lockedBy} covers this day`}>
                      <AppIcon kind="locked" size={14} />
                      <span className="sr-only">Locked by pay run {row.lockedBy}</span>
                    </span>
                  ) : row.fromLeave ? (
                    <span className="register__flag" title={`Approved leave ${row.fromLeave}`}>
                      <AppIcon kind="leave" size={14} />
                      <span className="sr-only">Approved leave</span>
                    </span>
                  ) : dirty ? (
                    <span className="register__dirty" title="Changed — not saved yet">
                      ●<span className="sr-only">Not saved yet</span>
                    </span>
                  ) : null}
                </td>
                <td>
                  <span className="mono">{row.code}</span>
                </td>
                <td>
                  {row.nameEn}
                  {row.nameUr && (
                    <>
                      {' '}
                      <span className="urdu">{row.nameUr}</span>
                    </>
                  )}
                  {!row.homeFarm && (
                    <>
                      {' '}
                      <span className="pill" title="Placed on another farm, working here today">
                        Visiting
                      </span>
                    </>
                  )}
                </td>
                <td className="dim">{row.designation ?? '—'}</td>
                <td className="dim">{WORKER_TYPE_TEXT[row.workerType]}</td>
                <td>
                  <div className="markseg" role="radiogroup" aria-label={`Mark for ${row.nameEn}`}>
                    {MARKS.map((m) => {
                      const on = v.mark === m;
                      const hint = suggested === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          aria-label={MARK_TEXT[m]}
                          data-suggested={hint ? 'true' : undefined}
                          tabIndex={-1}
                          disabled={!!locked}
                          title={hint ? `${MARK_TEXT[m]} — suggested, not saved yet` : MARK_TEXT[m]}
                          className={`markseg__btn markseg__btn--${m.toLowerCase()}${on ? ' markseg__btn--on' : ''}${hint ? ' markseg__btn--suggested' : ''}`}
                          onClick={() => setMark(row, m)}
                        >
                          {MARK_CODE[m]}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      role="radio"
                      aria-checked={v.mark === null}
                      aria-label="Not marked"
                      tabIndex={-1}
                      disabled={!!locked}
                      title="Not marked"
                      className={`markseg__btn markseg__btn--blank${v.mark === null ? ' markseg__btn--on' : ''}`}
                      onClick={() => setMark(row, null)}
                    >
                      –
                    </button>
                  </div>
                </td>
                <td>
                  {v.mark === 'LEAVE' ? (
                    <select
                      className="field__input register__select"
                      aria-label={`Which leave for ${row.nameEn}`}
                      data-leave-for={row.employeeId}
                      value={v.leaveTypeId ?? ''}
                      disabled={!!locked}
                      onChange={(e) => onChange(row, { leaveTypeId: e.target.value || null })}
                    >
                      <option value="">Which leave?</option>
                      {types.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nameEn}
                          {t.isPaid ? '' : ' (unpaid)'}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="dim">—</span>
                  )}
                </td>
                <td>
                  <HoursInput
                    label={`Overtime hours for ${row.nameEn}`}
                    value={v.overtimeHours}
                    disabled={!!locked || noWork(v.mark)}
                    why={locked ?? (noWork(v.mark) ? 'Overtime needs them to have worked that day' : undefined)}
                    onChange={(hours) => onChange(row, { overtimeHours: hours })}
                  />
                </td>
                <td>
                  {row.fromLeave ? (
                    <span className="dim">Approved leave {row.fromLeave}</span>
                  ) : (
                    <input
                      type="text"
                      className="field__input register__note"
                      aria-label={`Note for ${row.nameEn}`}
                      maxLength={300}
                      value={v.note}
                      disabled={!!locked}
                      onChange={(e) => onChange(row, { note: e.target.value })}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Overtime hours. Keeps what is typed ("0." on the way to "0.5") and shows
 * nothing rather than a 0 when there is none, so the column reads clean.
 */
function HoursInput({
  label,
  value,
  disabled,
  why,
  onChange,
}: {
  label: string;
  value: string;
  disabled: boolean;
  why: string | undefined;
  onChange: (hours: string) => void;
}) {
  const shown = (v: string) => (Number(v || 0) === 0 ? '' : v);
  const [text, setText] = useState(() => shown(value));
  const [last, setLast] = useState(value);
  // The value changed from outside (mark set to absent, changes undone):
  // follow it, unless it already means the same as what is typed.
  if (value !== last) {
    setLast(value);
    if (Number(value || 0) !== Number(text || 0)) setText(shown(value));
  }
  return (
    <input
      type="number"
      className="field__input register__ot"
      aria-label={label}
      min={0}
      max={24}
      step={0.5}
      inputMode="decimal"
      placeholder="0"
      value={text}
      disabled={disabled}
      title={why}
      onChange={(e) => {
        setText(e.target.value);
        onChange(e.target.value || '0');
      }}
    />
  );
}
